import React, { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
} from "antd";
import { DeleteFilled, ExclamationCircleFilled } from "@ant-design/icons";
import {
  useGetVendorsQuery,
  useGetCarsQuery,
  useGetSellersQuery,
  useGetCountriesQuery,
  useAddVendorRequestMutation,
  useGetVendorRequestsQuery,
  useDeleteVendorRequestMutation,
} from "../../../redux/Service/api";
import Heading from "../../../Components/Heading/Heading";
import ServerError from "../../../Components/utils/ServerError";
import dayjs from "dayjs";

interface VendorRequest {
  VendorName: string;
  CarName: string;
  SellerName: string;
  CountryFromName: string;
  CountryToName: string;
  RequestNo: number;
  RequestDate: string;
  Price: number;
}

const SupplierRequests: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const { data: vendorsData, isLoading: isVendorsLoading } =
    useGetVendorsQuery(undefined);
  const { data: carsData, isLoading: isCarsLoading } =
    useGetCarsQuery(undefined);
  const { data: sellersData, isLoading: isSellersLoading } =
    useGetSellersQuery(undefined);
  const { data: countriesData, isLoading: isCountriesLoading } =
    useGetCountriesQuery(undefined);

  const {
    data: VendorsRequestsData,
    isLoading: isVendorRequestsLoading,
    isError,
  } = useGetVendorRequestsQuery(undefined);

  const [addVendorRequest, { isLoading: isAdding }] =
    useAddVendorRequestMutation();

  const [deleteVendorRequest, { isLoading: delIsLoading }] =
    useDeleteVendorRequestMutation();
  const handleDeleteVendorRequest = (id: number) => {
    Modal.confirm({
      title: "هل أنت متأكد أنك تريد حذف هذا ",
      icon: <ExclamationCircleFilled />,
      content: "بالضغط على موافق سيتم حذف  بشكل دائم.",
      async onOk() {
        try {
          await deleteVendorRequest(id);
          message.success("تم حذف  بنجاح");
        } catch (error) {
          console.error(error);
          message.error("فشل في حذف .");
        }
      },
    });
  };

  const vendors = vendorsData?.ResponseObject ?? [];
  const cars = carsData?.ResponseObject ?? [];
  const sellers = sellersData?.ResponseObject ?? [];
  const countries = countriesData?.ResponseObject ?? [];
  const vendorRequests: VendorRequest[] =
    VendorsRequestsData?.ResponseObject ?? [];

  const handleAddRequest = async () => {
    try {
      const values = await form.validateFields();
      const requestPayload = {
        RequestDate: values.RequestDate.format("YYYY-MM-DD"),
        VendorID: values.VendorID,
        CarId: values.CarId,
        SellerId: values.SellerId,
        CountryFromID: values.CountryFromID,
        CountryToID: values.CountryToID,
        Price: values.Price,
      };

      await addVendorRequest(requestPayload);
      message.success("تمت إضافة طلب المورد بنجاح");
      form.resetFields();
      setOpenModal(false);
    } catch (error) {
      console.error("خطأ أثناء إضافة طلب المورد:", error);
      message.error("فشل في إضافة طلب المورد. يرجى المحاولة مرة أخرى.");
    }
  };

  const columns = [
    {
      title: "اسم المورد",
      dataIndex: "VendorName",
      key: "VendorName",
    },
    {
      title: "اسم السيارة",
      dataIndex: "CarId",
      key: "CarId",
      render: (value: any) =>
        cars.find((car: any) => car.CarID === value)?.Marka +
          " - " +
          cars.find((car: any) => car.CarID === value)?.Modal || "Unknown",
    },
    {
      title: "اسم مندوب التحصيل",
      dataIndex: "SellerName",
      key: "SellerName",
    },
    {
      title: "الدولة (من)",
      dataIndex: "CountryFromName",
      key: "CountryFromName",
    },
    {
      title: "الدولة (إلى)",
      dataIndex: "CountryToName",
      key: "CountryToName",
    },
    {
      title: "رقم الطلب",
      dataIndex: "RequestNo",
      key: "RequestNo",
    },
    {
      title: "تاريخ الطلب",
      dataIndex: "RequestDate",
      key: "RequestDate",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "السعر",
      dataIndex: "Price",
      key: "Price",
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<DeleteFilled />}
            danger
            onClick={() => handleDeleteVendorRequest(record.RequestNo)}
          />
        </div>
      ),
    },
  ];

  return (
    <section className="p-4 section_padding mt-0 pt-0 relative">
      <Heading heading="طلبات الموردين" />

      {isError ? <ServerError /> : null}

      <div className="text-center my-3">
        <Button
          className="w-[150px]"
          type="primary"
          onClick={() => setOpenModal(true)}
        >
          إضافة طلب
        </Button>
      </div>

      <div className="mx-auto">
        <Table
          columns={columns}
          dataSource={vendorRequests}
          rowKey="RequestNo"
          loading={isVendorRequestsLoading}
          pagination={{ pageSize: 5 }}
        />
      </div>

      <Modal
        title="إضافة طلب المورد"
        centered
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          name="add_vendor_request_form"
          onFinish={handleAddRequest}
        >
          <Form.Item
            label="تاريخ الطلب"
            name="RequestDate"
            rules={[{ required: true, message: "يرجى اختيار التاريخ" }]}
          >
            <DatePicker
              size="large"
              className="w-full"
              placeholder="اختر التاريخ"
            />
          </Form.Item>

          <Form.Item
            label="المورد"
            name="VendorID"
            rules={[{ required: true, message: "يرجى اختيار المورد" }]}
          >
            <Select size="large" placeholder="اختر المورد">
              {vendors.map((vendor: any) => (
                <Select.Option key={vendor.VendorID} value={vendor.VendorID}>
                  {vendor.VendorName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="السيارة"
            name="CarId"
            rules={[{ required: true, message: "يرجى اختيار السيارة" }]}
          >
            <Select size="large" placeholder="اختر السيارة">
              {cars.map((car: any) => (
                <Select.Option key={car.CarID} value={car.CarID}>
                  {car.Marka} -- {car.Modal} -- {car.CarNumber}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="مندوب تحصيل"
            name="SellerId"
            rules={[{ required: true, message: "يرجى اختيار مندوب تحصيل" }]}
          >
            <Select size="large" placeholder="اختر مندوب تحصيل">
              {sellers.map((seller: any) => (
                <Select.Option key={seller.SellerID} value={seller.SellerID}>
                  {seller.SellerName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="الدولة (من)"
            name="CountryFromID"
            rules={[{ required: true, message: "يرجى اختيار الدولة (من)" }]}
          >
            <Select size="large" placeholder="اختر الدولة (من)">
              {countries.map((country: any) => (
                <Select.Option
                  key={country.CountryID}
                  value={country.CountryID}
                >
                  {country.CountryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="الدولة (إلى)"
            name="CountryToID"
            rules={[{ required: true, message: "يرجى اختيار الدولة (إلى)" }]}
          >
            <Select size="large" placeholder="اختر الدولة (إلى)">
              {countries.map((country: any) => (
                <Select.Option
                  key={country.CountryID}
                  value={country.CountryID}
                >
                  {country.CountryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="السعر"
            name="Price"
            rules={[{ required: true, message: "يرجى إدخال السعر" }]}
          >
            <Input size="large" type="number" placeholder="أدخل السعر" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isAdding}
              className="w-full"
            >
              إضافة
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default SupplierRequests;
